import { useEffect, useState } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { usePageMeta } from '@/hooks/usePageMeta';
import { Mail, HeadphonesIcon, MessageCircle, Heart } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useTranslation } from 'react-i18next';
import ContactCaptureModal from '@/components/ContactCaptureModal';
import { EmailText } from '@/components/ObfuscationSafeEmail';

const CONTACT_DESCRIPTION =
  "Get in touch with Devotion Fund. Questions about fundraising, donations, or your account? We're here to help.";

export default function Contact() {
    usePageMeta('Contact', CONTACT_DESCRIPTION);
    const { t } = useTranslation();
    const { ref: contactRef, isVisible: contactVisible } = useScrollAnimation();
    const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation();
    const [openTopic, setOpenTopic] = useState<'general' | 'technical' | 'chat' | null>(null);

    const faqs = [
        { question: t('contactPage.faq.q1.question'), answer: t('contactPage.faq.q1.answer') },
        { question: t('contactPage.faq.q2.question'), answer: t('contactPage.faq.q2.answer') },
        { question: t('contactPage.faq.q3.question'), answer: t('contactPage.faq.q3.answer') },
        { question: t('contactPage.faq.q4.question'), answer: t('contactPage.faq.q4.answer') },
        { question: t('contactPage.faq.q5.question'), answer: t('contactPage.faq.q5.answer') },
    ];

    useEffect(() => {
        const jsonLd = {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
        };
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-contact-faq-jsonld', 'true');
        el.textContent = JSON.stringify(jsonLd);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, [faqs]);

    return (
        <div className="min-h-screen bg-background">
            <HomeHeader />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-6 bg-gradient-to-b from-secondary/20 to-background">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <Mail className="w-8 h-8 text-primary" />
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold text-primary mb-6">
                        {t('contactPage.hero.title')}
                    </h1>
                    <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
                        {t('contactPage.hero.subtitle')}
                    </p>
                </div>
            </section>

            {/* Mission Statement */}
            <section className="py-12 px-6">
                <div className="max-w-3xl mx-auto">
                    <div className="bg-gradient-to-br from-secondary/10 to-primary/5 rounded-3xl p-8 md:p-12 border border-border">
                        <div className="flex items-start gap-4 mb-4">
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                                <Heart className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-foreground mb-3">
                                    {t('contactPage.mission.title')}
                                </h2>
                                <p className="text-lg text-muted-foreground leading-relaxed">
                                    {t('contactPage.mission.description')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Options */}
            <section
                ref={contactRef}
                className={`py-20 px-6 transition-all duration-1000 ${contactVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
            >
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            {t('contactPage.howCanWeHelp.title')}
                        </h2>
                        <p className="text-lg text-muted-foreground">
                            {t('contactPage.howCanWeHelp.subtitle')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* General Questions */}
                        <div
                            className={`bg-white rounded-2xl p-8 shadow-sm border border-border hover:shadow-md transition-all duration-1000 ${contactVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                                }`}
                            style={{ transitionDelay: '200ms' }}
                        >
                            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 mx-auto">
                                <Mail className="w-7 h-7 text-primary" />
                            </div>
                            <h3 className="text-xl font-semibold text-foreground mb-3 text-center">
                                {t('contactPage.generalQuestions.title')}
                            </h3>
                            <p className="text-muted-foreground text-center mb-6 leading-relaxed">
                                {t('contactPage.generalQuestions.description')}
                            </p>
                            <div className="text-center">
                                <button
                                    onClick={() => setOpenTopic('general')}
                                    className="text-primary hover:text-primary/80 font-medium transition-colors inline-flex items-center justify-center"
                                >
                                    <EmailText text={t('contactPage.generalQuestions.email')} />
                                </button>
                            </div>
                        </div>

                        {/* Technical Support */}
                        <div
                            className={`bg-white rounded-2xl p-8 shadow-sm border border-border hover:shadow-md transition-all duration-1000 ${contactVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                                }`}
                            style={{ transitionDelay: '400ms' }}
                        >
                            <div className="w-14 h-14 bg-secondary/20 rounded-xl flex items-center justify-center mb-6 mx-auto">
                                <HeadphonesIcon className="w-7 h-7 text-secondary" />
                            </div>
                            <h3 className="text-xl font-semibold text-foreground mb-3 text-center">
                                {t('contactPage.technicalSupport.title')}
                            </h3>
                            <p className="text-muted-foreground text-center mb-6 leading-relaxed">
                                {t('contactPage.technicalSupport.description')}
                            </p>
                            <div className="text-center">
                                <button
                                    onClick={() => setOpenTopic('technical')}
                                    className="text-secondary hover:text-secondary/80 font-medium transition-colors inline-flex items-center justify-center"
                                >
                                    <EmailText text={t('contactPage.technicalSupport.email')} />
                                </button>
                            </div>
                        </div>

                        {/* Live Chat */}
                        <div
                            className={`bg-white rounded-2xl p-8 shadow-sm border border-border hover:shadow-md transition-all duration-1000 ${contactVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                                }`}
                            style={{ transitionDelay: '600ms' }}
                        >
                            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 mx-auto">
                                <MessageCircle className="w-7 h-7 text-primary" />
                            </div>
                            <h3 className="text-xl font-semibold text-foreground mb-3 text-center">
                                {t('contactPage.liveChat.title')}
                            </h3>
                            <p className="text-muted-foreground text-center mb-6 leading-relaxed">
                                {t('contactPage.liveChat.description')}
                            </p>
                            <div className="text-center">
                                <Button
                                    onClick={() => setOpenTopic('chat')}
                                    className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold"
                                >
                                    {t('contactPage.liveChat.button')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Response Time Note */}
            <section className="py-12 px-6 bg-gradient-to-br from-primary/5 to-secondary/10">
                <div className="max-w-3xl mx-auto text-center">
                    <p className="text-muted-foreground leading-relaxed">
                        <strong>{t('contactPage.responseTime.title')}</strong>{' '}
                        <EmailText text={t('contactPage.responseTime.description')} />
                    </p>
                </div>
            </section>

            {/* FAQ */}
            <section
                ref={faqRef}
                className={`py-20 px-6 transition-all duration-1000 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
            >
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            {t('contactPage.faq.title')}
                        </h2>
                        <p className="text-lg text-muted-foreground">
                            {t('contactPage.faq.subtitle')}
                        </p>
                    </div>
                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl p-6 shadow-sm border border-border"
                            >
                                <h3 className="text-lg font-semibold text-foreground mb-2">
                                    {faq.question}
                                </h3>
                                <p className="text-muted-foreground leading-relaxed">
                                    <EmailText text={faq.answer} />
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />

            <ContactCaptureModal
                isOpen={openTopic === 'general'}
                onClose={() => setOpenTopic(null)}
                topic="general"
                title={t('contactPage.generalQuestions.title')}
                description={t('contactPage.generalQuestions.description')}
                email={t('contactPage.generalQuestions.email')}
            />
            <ContactCaptureModal
                isOpen={openTopic === 'technical'}
                onClose={() => setOpenTopic(null)}
                topic="technical"
                title={t('contactPage.technicalSupport.title')}
                description={t('contactPage.technicalSupport.description')}
                email={t('contactPage.technicalSupport.email')}
            />
            <ContactCaptureModal
                isOpen={openTopic === 'chat'}
                onClose={() => setOpenTopic(null)}
                topic="chat"
                title={t('contactPage.liveChat.title')}
                description={t('contactPage.liveChat.description')}
                email={t('contactPage.liveChat.email')}
            />
        </div>
    );
}


